import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, apiErrorMessage, PillButton } from '@aralere/shared-ui';
import type { Client, TimeEntry } from '@aralere/shared-types';

// The quick Start/End timer controls (slice 3.2, s49) — the SAME component on the dashboard and
// the Time page, per the owner's spec: big touch-friendly pills, soft green/red with a gentle
// glow, and OBVIOUS pressed-and-registered feedback (press = scale-down; in-flight = "Starting…"/
// "Ending…"; registered = a 1.5s "Started"/"Stopped" confirmation with a brighter glow pulse,
// plus the running chip appearing/updating immediately). Open timers are DRAFT timeEntries
// (excluded from totals/draws until completed).
//
// Behaviors by mode:
// - dashboard: Start opens an inline client picker, then starts; End stops the OLDEST open timer
//   across ALL clients and deep-links to /admin/time?client=<id>&complete=<entryId> so the full
//   entry info is filled in on the Time page (the owner's simplified-dashboard rule). Running
//   chips deep-link the same way.
// - time: the host page owns the client select + the Running section; Start uses the selected
//   client; End stops the selected client's oldest open timer (or the oldest overall when no
//   client is selected) and hands the entry to the host to open the completion form in place.

export interface QuickTimerProps {
  mode: 'dashboard' | 'time';
  /** time mode: the page's selected client (Start targets it; End prefers its oldest open). */
  selectedClientId?: string;
  /** time mode: called after Start/End so the host refreshes its Running section + entries. */
  onChanged?: () => void;
  /** time mode: called with the stopped entry so the host opens the completion form. */
  onCompleteRequested?: (entry: TimeEntry) => void;
}

type Flash = { kind: 'started' | 'stopped'; at: number } | null;

// A timer's total elapsed = banked seconds + the current running segment (0 when paused).
export function timerElapsedSeconds(t: TimeEntry, now: number): number {
  const banked = t.accumulatedSeconds ?? 0;
  const seg = t.paused !== true && t.startAt ? Math.max(0, Math.floor((now - Date.parse(t.startAt)) / 1000)) : 0;
  return banked + seg;
}
export const isRunning = (t: TimeEntry): boolean => t.paused !== true && !!t.startAt;
const hm = (secs: number): string => {
  const mins = Math.floor(secs / 60);
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
};

export function QuickTimer({ mode, selectedClientId, onChanged, onCompleteRequested }: QuickTimerProps): JSX.Element {
  const navigate = useNavigate();
  const [open, setOpen] = useState<TimeEntry[]>([]);
  const [clients, setClients] = useState<Client[] | null>(null);
  const [picking, setPicking] = useState(false);
  const [pickedClient, setPickedClient] = useState('');
  const [busy, setBusy] = useState<'start' | 'end' | null>(null);
  const [flash, setFlash] = useState<Flash>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const flashTimer = useRef<number | null>(null);

  const refreshOpen = async (): Promise<void> => {
    try {
      const res = await api.admin.listOpenTimers();
      setOpen(res.timers);
    } catch {
      /* the chips are a convenience; the buttons surface real errors */
    }
  };

  useEffect(() => {
    void refreshOpen();
  }, []);

  // A live minute tick while anything is running (only running timers, i.e. endAt null, tick).
  useEffect(() => {
    if (!open.some((t) => isRunning(t))) return;
    const id = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(id);
  }, [open]);

  useEffect(() => () => {
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
  }, []);

  const registerFlash = (kind: 'started' | 'stopped'): void => {
    setFlash({ kind, at: Date.now() });
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlash(null), 1500);
  };

  const clientName = (id: string): string =>
    clients?.find((c) => c.id === id)?.displayName ?? id;

  const ensureClients = async (): Promise<Client[]> => {
    if (clients) return clients;
    const res = await api.admin.listClients();
    const list = res.clients.map((r) => r.client);
    setClients(list);
    return list;
  };

  // Chips want names too (both modes) — load lazily once there is something to label.
  useEffect(() => {
    if (open.length > 0 && !clients) void ensureClients().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open.length]);

  const startFor = async (cid: string): Promise<void> => {
    setBusy('start');
    setError(null);
    try {
      await api.admin.startTimer({ clientId: cid, draft: true });
      setPicking(false);
      setPickedClient('');
      await refreshOpen();
      registerFlash('started');
      onChanged?.();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const onStart = async (): Promise<void> => {
    setError(null);
    if (mode === 'time' && selectedClientId) {
      await startFor(selectedClientId);
      return;
    }
    // Dashboard (or time with no client picked): inline picker.
    try {
      await ensureClients();
      setPicking(true);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  };

  const onEnd = async (): Promise<void> => {
    setError(null);
    // Oldest OPEN timer, preferring the selected client's (time mode) and a running one over a
    // paused one. End = finish: bank any running segment (pause), then hand off to completion.
    const pool = [...open].sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1));
    const preferred =
      (mode === 'time' && selectedClientId ? pool.find((t) => t.clientId === selectedClientId && isRunning(t)) : undefined) ??
      pool.find((t) => isRunning(t)) ??
      (mode === 'time' && selectedClientId ? pool.find((t) => t.clientId === selectedClientId) : undefined) ??
      pool[0];
    if (!preferred) {
      setError('No open timer to end.');
      return;
    }
    setBusy('end');
    try {
      const res = isRunning(preferred)
        ? await api.admin.updateTimeEntry(preferred.id, preferred.clientId, { paused: true })
        : { entry: preferred };
      await refreshOpen();
      registerFlash('stopped');
      if (mode === 'dashboard') {
        navigate(`/admin/time?client=${encodeURIComponent(preferred.clientId)}&complete=${encodeURIComponent(preferred.id)}`);
      } else {
        onChanged?.();
        onCompleteRequested?.(res.entry);
      }
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const togglePause = async (t: TimeEntry): Promise<void> => {
    setError(null);
    try {
      await api.admin.updateTimeEntry(t.id, t.clientId, { paused: isRunning(t) });
      await refreshOpen();
      onChanged?.();
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  };

  const running = open.filter((t) => isRunning(t));
  const paused = open.filter((t) => !isRunning(t));

  const base =
    'inline-flex min-h-12 flex-1 items-center justify-center gap-2 rounded-[var(--pill-radius)] border px-6 py-3 text-base font-semibold transition ' +
    'active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-50 md:flex-none md:min-w-44';
  const startCls =
    flash?.kind === 'started'
      ? 'border-[var(--tint-green-border)] bg-[var(--tint-green-bg)] text-[var(--tint-green-text)] shadow-[var(--pill-glow-green-flash)]'
      : 'border-[var(--tint-green-border)] bg-[var(--tint-green-bg-soft)] text-[var(--tint-green-text)] shadow-[var(--pill-glow-green)] hover:bg-[var(--tint-green-bg)]';
  const endCls =
    flash?.kind === 'stopped'
      ? 'border-[var(--tint-red-border)] bg-[var(--tint-red-bg)] text-[var(--tint-red-text)] shadow-[var(--pill-glow-red-flash)]'
      : 'border-[var(--tint-red-border)] bg-[var(--tint-red-bg-soft)] text-[var(--tint-red-text)] shadow-[var(--pill-glow-red)] hover:bg-[var(--tint-red-bg)]';

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <button type="button" className={`${base} ${startCls}`} disabled={busy !== null} onClick={() => void onStart()}>
          {busy === 'start' ? 'Starting…' : flash?.kind === 'started' ? 'Started' : 'Start Time'}
        </button>
        <button
          type="button"
          className={`${base} ${endCls}`}
          disabled={busy !== null || open.length === 0}
          onClick={() => void onEnd()}
        >
          {busy === 'end' ? 'Ending…' : flash?.kind === 'stopped' ? 'Stopped' : 'End Time'}
        </button>
      </div>

      {picking ? (
        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <select
            value={pickedClient}
            onChange={(e) => setPickedClient(e.target.value)}
            className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)] px-3 py-2 text-sm md:min-w-64"
          >
            <option value="">Which client is this time for?</option>
            {(clients ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.displayName}
                {c.isInternal ? ' (internal)' : ''}
              </option>
            ))}
          </select>
          <div className="flex gap-2">
            <PillButton size="lg" tone="green" disabled={!pickedClient || busy !== null} onClick={() => void startFor(pickedClient)}>
              Start
            </PillButton>
            <PillButton
              size="lg"
              onClick={() => {
                setPicking(false);
                setPickedClient('');
              }}
            >
              Cancel
            </PillButton>
          </div>
        </div>
      ) : null}

      {error ? <p className="text-sm text-[var(--tint-red-text)]">{error}</p> : null}

      {mode === 'dashboard' && (running.length > 0 || paused.length > 0) ? (
        <div className="flex flex-wrap gap-2">
          {running.map((t) => (
            <span key={t.id} className="inline-flex items-center gap-2 rounded-full border border-[var(--tint-green-border)] bg-[var(--tint-green-bg-soft)] py-1 pl-3 pr-1.5 text-sm text-[var(--tint-green-text)]">
              <span className="h-2 w-2 animate-pulse rounded-full bg-green-500" aria-hidden="true" />
              <button type="button" className="hover:underline" onClick={() => navigate(`/admin/time?client=${encodeURIComponent(t.clientId)}`)}>
                {clientName(t.clientId)}: {hm(timerElapsedSeconds(t, now))}
              </button>
              <PillButton size="xs" tone="amber" onClick={() => void togglePause(t)}>
                Pause
              </PillButton>
              <PillButton
                size="xs"
                onClick={() => navigate(`/admin/time?client=${encodeURIComponent(t.clientId)}&complete=${encodeURIComponent(t.id)}`)}
              >
                Complete
              </PillButton>
            </span>
          ))}
          {paused.map((t) => (
            <span key={t.id} className="inline-flex items-center gap-2 rounded-full border border-[var(--tint-warn-border)] bg-[var(--tint-warn-bg-soft)] py-1 pl-3 pr-1.5 text-sm text-[var(--tint-warn-text)]">
              <span className="h-2 w-2 rounded-full bg-amber-500" aria-hidden="true" />
              <button type="button" className="hover:underline" onClick={() => navigate(`/admin/time?client=${encodeURIComponent(t.clientId)}`)}>
                {clientName(t.clientId)}: {hm(timerElapsedSeconds(t, now))} (paused)
              </button>
              <PillButton size="xs" tone="green" onClick={() => void togglePause(t)}>
                Resume
              </PillButton>
              <PillButton
                size="xs"
                onClick={() => navigate(`/admin/time?client=${encodeURIComponent(t.clientId)}&complete=${encodeURIComponent(t.id)}`)}
              >
                Complete
              </PillButton>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
